import { Loader, Menu } from "@mantine/core";
import IconCardPlus from "@src/assets/icons/lucide/card-plus.svg?react";
import IconMessageSquare from "@src/assets/icons/lucide/message-square.svg?react";
import IconAdjustmentsHorizontal from "@src/assets/icons/tabler/adjustments-horizontal.svg?react";
import IconFolderPlus from "@src/assets/icons/tabler/folder-plus.svg?react";
import IconPlus from "@src/assets/icons/tabler/plus.svg?react";
import { shareKeySignal } from "@src/auth/auth-utils";
import { ROUTES } from "@src/common/constants";
import { useOptionalAgentChatContext } from "@src/components/agent-chat/hooks/agent-chat-context";
import { useCreateCardInWorkspace } from "@src/components/board/mutations/create-card-in-workspace";
import { useCreateWhiteboard } from "@src/components/board/mutations/create-whiteboard";
import { fetchActiveWhiteboardsQueryOptions } from "@src/components/board/queries/fetch-active-whiteboards";
import { useWhiteboardNavigation } from "@src/components/board/use-whiteboard-navigation";
import {
	generateWhiteboardName,
	getNextNamedItemIndex,
} from "@src/components/board/utils";
import {
	WhiteboardDocumentId,
	parseWhiteboardDocumentId,
} from "@src/components/collaboration/document-id";
import { useLibraryEntities } from "@src/components/library/queries/library-entities";
import { useCreateBoardModal } from "@src/components/modals/create-board/use-create-board-modal";
import { FeatureGate } from "@src/components/plan/feature-gate/feature-gate";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import { useAuthenticatedUser } from "@src/components/user/user-context";
import { NavbarSection, SupportedFeature } from "@src/gql/graphql";
import { useIsDesktop } from "@src/hooks/use-is-desktop";
import { captureBoardCreationSucceeded } from "@src/utils/analytics";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { nanoid } from "nanoid";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNavActiveSource } from "../context/nav-active-source-context";
import { useNavPendingRename } from "../context/nav-pending-rename-context";
import { ManageBoardsModal } from "./manage-boards-modal";
import { useRanklistTree } from "./use-ranklist-tree";

function CreateBusyIcon({
	busy,
	className,
}: {
	busy: boolean;
	className?: string;
}) {
	if (busy) {
		return (
			<Loader
				size={12}
				color="currentColor"
				className={className}
				aria-hidden
			/>
		);
	}
	return <IconPlus className={className} />;
}

function CreateWhiteboardButton({
	disabled,
	compact = false,
	merged = false,
	section = NavbarSection.MyWhiteboards,
}: {
	disabled?: boolean;
	compact?: boolean;
	merged?: boolean;
	section?: NavbarSection;
}) {
	const isDesktop = useIsDesktop();
	const authenticatedUser = useAuthenticatedUser();
	const navigate = useNavigate();
	const { setActiveSource } = useNavActiveSource();
	const agentChat = useOptionalAgentChatContext();
	const { open: openCreateBoardModal } = useCreateBoardModal();
	const { navigateToWhiteboard } = useWhiteboardNavigation();
	const { mutateAsync: createWhiteboard, isPending: isCreatingWhiteboard } =
		useCreateWhiteboard();
	const isSharedSection = section === NavbarSection.SharedWithMe;

	const { handleCreateFolder, isCreatingFolder } = useRanklistTree(section);
	const { requestRename } = useNavPendingRename();

	const { mutateAsync: createCardInWorkspace, isPending: isCreatingCard } =
		useCreateCardInWorkspace();
	const { data: libraryCardsData } = useLibraryEntities({ type: "card" });
	const libraryCards = useMemo(
		() => libraryCardsData?.pages.flatMap((p) => p.nodes),
		[libraryCardsData],
	);
	const highestCardIndexRef = useRef(0);

	const [manageBoardsOpen, setManageBoardsOpen] = useState(false);

	const { data: activeWhiteboards } = useQuery(
		fetchActiveWhiteboardsQueryOptions(
			authenticatedUser.id,
			shareKeySignal.value,
		),
	);

	const highestBoardIndexRef = useRef(0);
	const isBusy = isCreatingWhiteboard || isCreatingCard;

	const handleCreateBlankBoard = async () => {
		if (isBusy) return;

		const whiteboardId = new WhiteboardDocumentId();
		const nextIndex = getNextNamedItemIndex(
			activeWhiteboards,
			highestBoardIndexRef,
		);

		const { data } = await createWhiteboard({
			id: whiteboardId,
			name: generateWhiteboardName(nextIndex),
		});

		if (data?.id) {
			captureBoardCreationSucceeded(
				parseWhiteboardDocumentId(data.id),
				"blank",
			);
			setActiveSource("my-boards");
			navigateToWhiteboard(data.id);
		}
	};

	const handleCreateNewFolder = async () => {
		const createdFolderId = await handleCreateFolder(null, {
			id: nanoid(),
			isFolder: true,
			name: "New Folder",
		});
		if (createdFolderId) {
			requestRename(createdFolderId);
		}
	};

	const handleCreateNewCard = async () => {
		if (isBusy) return;

		const newCardId = `Card-${nanoid()}`;
		const nextIndex = getNextNamedItemIndex(libraryCards, highestCardIndexRef);

		const createdCard = await createCardInWorkspace({
			section,
			name: `Card #${nextIndex}`,
			cardId: newCardId,
		});
		if (createdCard?.key) {
			requestRename(createdCard.key);
			setActiveSource("my-boards");
			navigate(`/${newCardId}`);
		}
	};

	const handleCreateNewChat = useCallback(() => {
		agentChat?.startNewChat();
		navigate(ROUTES.ask);
		requestAnimationFrame(() => {
			document
				.querySelector<HTMLTextAreaElement>(
					'[data-ui-id="agent-chat-home"] textarea',
				)
				?.focus();
		});
	}, [agentChat, navigate]);

	const button = compact ? (
		<button
			type="button"
			disabled={isBusy || !!disabled}
			onClick={(event) => event.stopPropagation()}
			aria-label={isBusy ? "Creating…" : "Create"}
			aria-busy={isBusy}
			className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-inverse transition-colors bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 cursor-pointer"
		>
			<CreateBusyIcon busy={isBusy} className="h-4 w-4 shrink-0" />
		</button>
	) : merged ? (
		<button
			type="button"
			disabled={isBusy || !!disabled}
			onClick={(event) => event.stopPropagation()}
			aria-label={isBusy ? "Creating…" : "Create"}
			aria-busy={isBusy}
			className={clsx(
				"flex h-full w-full items-center justify-center gap-1.5 px-2.5",
				"text-xs font-semibold text-brand-primary transition-colors",
				"hover:bg-brand-primary/12 disabled:opacity-50",
				"cursor-pointer select-none",
			)}
		>
			<CreateBusyIcon
				busy={isBusy}
				className="h-3.5 w-3.5 shrink-0 text-brand-primary"
			/>
			<span>{isBusy ? "Creating…" : "Create"}</span>
		</button>
	) : (
		<button
			type="button"
			disabled={isBusy || !!disabled}
			onClick={(event) => event.stopPropagation()}
			aria-label={isBusy ? "Creating…" : "Create"}
			aria-busy={isBusy}
			className={clsx(
				"flex h-8 w-full items-center justify-center gap-1.5 rounded-lg px-3",
				"text-xs font-semibold text-text-inverse transition-colors",
				"bg-brand-primary hover:bg-brand-primary-hover",
				"disabled:opacity-50 cursor-pointer",
			)}
		>
			<CreateBusyIcon busy={isBusy} className="h-3.5 w-3.5 shrink-0" />
			{isBusy ? "Creating…" : "Create"}
		</button>
	);

	if (disabled) {
		return <Tooltip label="Upgrade to create more boards ✨">{button}</Tooltip>;
	}

	return (
		<>
			<Menu
				position={isDesktop ? "right-start" : "bottom"}
				shadow="md"
				radius="md"
				withinPortal
				zIndex={3100}
			>
				{compact ? (
					<Tooltip label="Create" openDelay={300} withArrow>
						<Menu.Target>{button}</Menu.Target>
					</Tooltip>
				) : (
					<Menu.Target>{button}</Menu.Target>
				)}
				<Menu.Dropdown>
					<Menu.Item
						leftSection={
							isCreatingWhiteboard ? (
								<Loader size={12} color="currentColor" />
							) : (
								<IconPlus width={15} height={15} />
							)
						}
						onClick={handleCreateBlankBoard}
						disabled={isBusy}
					>
						{isCreatingWhiteboard ? "Creating board…" : "New board"}
					</Menu.Item>
					<Menu.Item
						leftSection={<IconFolderPlus width={15} height={15} />}
						onClick={handleCreateNewFolder}
						disabled={isCreatingFolder || isBusy}
					>
						New folder
					</Menu.Item>
					{!isSharedSection && (
						<Menu.Item
							leftSection={
								isCreatingCard ? (
									<Loader size={12} color="currentColor" />
								) : (
									<IconCardPlus width={15} height={15} />
								)
							}
							onClick={handleCreateNewCard}
							disabled={isBusy}
						>
							{isCreatingCard ? "Creating card…" : "New card"}
						</Menu.Item>
					)}
					{!isSharedSection && (
						<Menu.Item
							leftSection={<IconMessageSquare width={15} height={15} />}
							onClick={handleCreateNewChat}
						>
							New chat
						</Menu.Item>
					)}
					<Menu.Divider />
					<Menu.Item
						leftSection={<IconAdjustmentsHorizontal width={15} height={15} />}
						onClick={() => setManageBoardsOpen(true)}
					>
						{isSharedSection ? "Manage shared workspace" : "Manage workspace"}
					</Menu.Item>
					<Menu.Item onClick={openCreateBoardModal} disabled={isBusy}>
						Create from examples
					</Menu.Item>
				</Menu.Dropdown>
			</Menu>

			<ManageBoardsModal
				opened={manageBoardsOpen}
				onClose={() => setManageBoardsOpen(false)}
				section={section}
			/>
		</>
	);
}

function MergedCreatePlaceholder() {
	return (
		<div
			aria-hidden
			className={clsx(
				"flex h-full w-full items-center justify-center gap-1.5 px-2.5",
				"text-xs font-semibold text-brand-primary select-none",
			)}
		>
			<IconPlus className="h-3.5 w-3.5 shrink-0 text-brand-primary" />
			<span>Create</span>
		</div>
	);
}

export function NavTreeCreateBoardRow({
	compact = false,
	merged = false,
	section,
}: {
	compact?: boolean;
	merged?: boolean;
	section?: NavbarSection;
}) {
	return (
		<FeatureGate
			feature={SupportedFeature.CreateBoards}
			suspenseComponent={merged ? <MergedCreatePlaceholder /> : null}
			render={({ enabled, stats }) => (
				<CreateWhiteboardButton
					disabled={!enabled || stats?.available === false}
					compact={compact}
					merged={merged}
					section={section}
				/>
			)}
		/>
	);
}
